import React, {useEffect, useMemo, useState} from "react";
import {
  Board,
  BoardHeader,
  BoardSearchResult,
  CancelButton,
  Input,
  SubmitContainer
} from "../../../assets/GlobalStyles";
import {AdverInfo} from "../styles/common";
import {Controller, useFormContext} from "react-hook-form";
import {useAtom} from "jotai";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import ImageUploading from "react-images-uploading";
import queryString from "query-string";
import styled from "styled-components";
import {toast} from "react-toastify";
import {CampaignStepForm, CampaignStepSidebar} from "./CampaignStepDesign";
import CampaignSettingSummary from "../CampaignSettingSummary";

import {stepCampaignAtom} from "../entity";
import {campaignBasicInfoAtom} from "../entity/Info";
import {
  bannerSizeAtom,
  campaignCreativeAtom
} from "../entity/Creative";
import {selEnumInfo} from "../../../services/campaign/InfoAxios";
import {
  selCreativeAudioInfo,
  selCreativeBannerInfo,
  selCreativePopUnderInfo,
  updateCampaignAudio,
  updateCampaignBanner,
  updateCampaignPopUnder,
  uploadAudioFile,
  uploadBannerImages
} from "../../../services/campaign/CreativeAxios";
import {
  dateFormat,
  toDay
} from "../../../common/StringUtils";
import {AudioEditor} from "../Frame/AudioEditor";

const MATERIAL_GROUP_TYPE = {
  AUDIO: "AUDIO",
  BANNER: "BANNER",
  LINK: "LINK"
};

const MATERIAL_GROUP_OPTIONS = [
  {
    value: MATERIAL_GROUP_TYPE.AUDIO,
    label: "오디오",
    description: "오디오 파일을 등록합니다.",
    icon: "♪",
    disabled: false
  },
  {
    value: MATERIAL_GROUP_TYPE.BANNER,
    label: "배너",
    description: "이미지와 랜딩 URL을 등록합니다.",
    icon: "▧",
    disabled: true
  },
  {
    value: MATERIAL_GROUP_TYPE.LINK,
    label: "링크",
    description: "랜딩 URL만 등록합니다.",
    icon: "↗",
    disabled: true
  }
];

const URL_PATTERN = /^https?:\/\/.+/i;

const normalizeMaterialType = creativeType => {
  if (creativeType === MATERIAL_GROUP_TYPE.BANNER) {
    return MATERIAL_GROUP_TYPE.BANNER;
  }

  if (
      creativeType === MATERIAL_GROUP_TYPE.LINK ||
      creativeType === "POP_UNDER"
  ) {
    return MATERIAL_GROUP_TYPE.LINK;
  }

  return MATERIAL_GROUP_TYPE.AUDIO;
};

const buildCreativeName = materialType => {
  const createdAt = dateFormat(
      toDay(),
      "YYMMDDHHmm"
  );

  return `${materialType}_${createdAt}`;
};

function LandingUrlEditor({
  control,
  errors,
  campaignCreativeInfo,
  onChangeInput,
  title,
  description
}) {
  return (
      <SettingCard>
        <SectionHeader>
          <SectionTitle>{title}</SectionTitle>

          <SectionGuide>
            {description}
          </SectionGuide>
        </SectionHeader>

        <InnerBox>
          <FieldRow>
            <FieldLabel>
              PC 랜딩 URL
            </FieldLabel>

            <FieldContent>
              <Controller
                  name="pcLandingUrl"
                  control={control}
                  shouldUnregister
                  rules={{
                    required:
                        "PC 랜딩 URL을 입력해주세요.",
                    pattern: {
                      value: URL_PATTERN,
                      message:
                          "http:// 또는 https://가 포함된 URL을 입력해주세요."
                    }
                  }}
                  render={({field}) => (
                      <SettingInput
                          {...field}
                          type="text"
                          name="pcLandingUrl"
                          placeholder="https://example.com"
                          value={
                              campaignCreativeInfo
                                  ?.pcLandingUrl ?? ""
                          }
                          onChange={event => {
                            field.onChange(event);
                            onChangeInput(event);
                          }}
                      />
                  )}
              />

              {errors.pcLandingUrl && (
                  <ValidationMessage>
                    {
                      errors.pcLandingUrl
                          .message
                    }
                  </ValidationMessage>
              )}
            </FieldContent>
          </FieldRow>

          <FieldRow>
            <FieldLabel>
              MOBILE 랜딩 URL
            </FieldLabel>

            <FieldContent>
              <Controller
                  name="mobLandingUrl"
                  control={control}
                  shouldUnregister
                  rules={{
                    required:
                        "MOBILE 랜딩 URL을 입력해주세요.",
                    pattern: {
                      value: URL_PATTERN,
                      message:
                          "http:// 또는 https://가 포함된 URL을 입력해주세요."
                    }
                  }}
                  render={({field}) => (
                      <SettingInput
                          {...field}
                          type="text"
                          name="mobLandingUrl"
                          placeholder="https://m.example.com"
                          value={
                              campaignCreativeInfo
                                  ?.mobLandingUrl ?? ""
                          }
                          onChange={event => {
                            field.onChange(event);
                            onChangeInput(event);
                          }}
                      />
                  )}
              />

              {errors.mobLandingUrl && (
                  <ValidationMessage>
                    {
                      errors.mobLandingUrl
                          .message
                    }
                  </ValidationMessage>
              )}
            </FieldContent>
          </FieldRow>
        </InnerBox>
      </SettingCard>
  );
}

function BannerImageEditor({
  bannerSizes,
  campaignCreativeInfo,
  setCampaignCreative,
  clearErrors,
  errors,
  onImageError
}) {
  const [selectedBannerSize, setSelectedBannerSize] =
      useState("");

  useEffect(() => {
    if (
        !selectedBannerSize &&
        bannerSizes?.length > 0
    ) {
      const initialSize =
          campaignCreativeInfo
              ?.materials?.[0]
              ?.bannerSize ??
          bannerSizes[0].value;

      setSelectedBannerSize(
          initialSize
      );
    }
  }, [
    bannerSizes,
    campaignCreativeInfo,
    selectedBannerSize
  ]);

  const selectedMaterial = useMemo(
      () =>
          (
              campaignCreativeInfo
                  ?.materials ?? []
          ).find(
              material =>
                  material.bannerSize ===
                  selectedBannerSize
          ),
      [
        campaignCreativeInfo,
        selectedBannerSize
      ]
  );

  const selectedImages =
      selectedMaterial?.images ?? [];

  const ensureMaterial = bannerSize => {
    const materials =
        campaignCreativeInfo
            ?.materials ?? [];

    if (
        materials.some(
            material =>
                material.bannerSize ===
                bannerSize
        )
    ) {
      return materials;
    }

    return [
      ...materials,
      {
        bannerSize,
        images: []
      }
    ];
  };

  const handleSelectBannerSize =
      bannerSize => {
        setSelectedBannerSize(
            bannerSize
        );

        setCampaignCreative(prev => ({
          ...prev,
          materials: ensureMaterial(
              bannerSize
          )
        }));
      };

  const handleUploadImages =
      async pictureFiles => {
        if (
            !selectedBannerSize ||
            pictureFiles.length === 0
        ) {
          return;
        }

        if (
            selectedImages.length +
            pictureFiles.length >
            5
        ) {
          toast.warning(
              "사이즈별 이미지는 최대 5개까지 등록할 수 있습니다.",
              {
                toastId:
                    "bannerImageMaxCount"
              }
          );

          return;
        }

        try {
          const formData =
              new FormData();

          pictureFiles.forEach(item => {
            formData.append(
                "images",
                item.file,
                item.file.name
            );
          });

          const response =
              await uploadBannerImages(
                  formData,
                  selectedBannerSize
              );

          if (!response?.images) {
            throw new Error(
                "배너 이미지 업로드 응답이 없습니다."
            );
          }

          setCampaignCreative(prev => {
            const materials =
                (
                    prev?.materials ?? []
                ).some(
                    material =>
                        material.bannerSize ===
                        selectedBannerSize
                )
                    ? prev.materials
                    : [
                      ...(prev?.materials ??
                          []),
                      {
                        bannerSize:
                        selectedBannerSize,
                        images: []
                      }
                    ];

            return {
              ...prev,
              materials:
                  materials.map(
                      material =>
                          material.bannerSize ===
                          selectedBannerSize
                              ? {
                                ...material,
                                images: [
                                  ...(
                                      material.images ??
                                      []
                                  ),
                                  ...response.images
                                ]
                              }
                              : material
                  )
            };
          });

          clearErrors("materials");
        } catch (error) {
          console.error(
              "배너 이미지 업로드 실패:",
              error
          );

          toast.error(
              "배너 이미지 업로드 중 오류가 발생했습니다.",
              {
                toastId:
                    "bannerImageUploadError"
              }
          );
        }
      };

  const handleDeleteImage =
      imagePath => {
        setCampaignCreative(prev => ({
          ...prev,
          materials:
              (
                  prev?.materials ?? []
              ).map(material =>
                  material.bannerSize ===
                  selectedBannerSize
                      ? {
                        ...material,
                        images:
                            (
                                material.images ??
                                []
                            ).filter(
                                image =>
                                    image.imagePath !==
                                    imagePath
                            )
                      }
                      : material
              )
        }));
      };

  return (
      <SettingCard>
        <SectionHeader>
          <SectionTitle>
            배너 이미지 등록
          </SectionTitle>

          <SectionGuide>
            배너 사이즈별 이미지를 최대
            5개까지 등록할 수 있습니다.
          </SectionGuide>
        </SectionHeader>

        <InnerBox>
          <FieldRow>
            <FieldLabel>
              배너 사이즈
            </FieldLabel>

            <FieldContent>
              <BannerSizeButtons>
                {(bannerSizes ?? []).map(
                    option => {
                      const active =
                          selectedBannerSize ===
                          option.value;

                      return (
                          <BannerSizeButton
                              key={option.value}
                              type="button"
                              $active={active}
                              onClick={() =>
                                  handleSelectBannerSize(
                                      option.value
                                  )
                              }
                          >
                            {option.label}
                          </BannerSizeButton>
                      );
                    }
                )}
              </BannerSizeButtons>
            </FieldContent>
          </FieldRow>

          <FieldRow>
            <FieldLabel>
              이미지
            </FieldLabel>

            <FieldContent>
              <ImageUploadArea>
                {selectedImages.map(
                    image => (
                        <ImageCard
                            key={image.imagePath}
                        >
                          <DeleteImageButton
                              type="button"
                              onClick={() =>
                                  handleDeleteImage(
                                      image.imagePath
                                  )
                              }
                          >
                            ×
                          </DeleteImageButton>

                          <ImagePreview
                              style={{
                                backgroundImage:
                                    `url(${image.imagePath})`
                              }}
                          />
                        </ImageCard>
                    )
                )}

                {selectedImages.length <
                    5 && (
                        <ImageUploading
                            multiple
                            acceptType={[
                              "jpg",
                              "jpeg",
                              "gif",
                              "png"
                            ]}
                            maxFileSize={
                              10485760
                            }
                            maxNumber={5}
                            onChange={
                              handleUploadImages
                            }
                            onError={
                              onImageError
                            }
                        >
                          {({
                            onImageUpload
                          }) => (
                              <AddImageButton
                                  type="button"
                                  onClick={
                                    onImageUpload
                                  }
                              >
                                <AddImageIcon>
                                  +
                                </AddImageIcon>

                                <span>
                        이미지 등록
                      </span>
                              </AddImageButton>
                          )}
                        </ImageUploading>
                    )}
              </ImageUploadArea>

              {errors.materials && (
                  <ValidationMessage>
                    {
                      errors.materials
                          .message
                    }
                  </ValidationMessage>
              )}
            </FieldContent>
          </FieldRow>
        </InnerBox>
      </SettingCard>
  );
}

export function CampaignFour() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [, setStepCampaign] =
      useAtom(stepCampaignAtom);

  const [
    campaignCreativeInfo,
    setCampaignCreative
  ] = useAtom(campaignCreativeAtom);

  const [
    campaignBasicInfo,
    setCampaignBasicInfo
  ] = useAtom(campaignBasicInfoAtom);

  const [
    bannerSizes,
    setBannerSize
  ] = useAtom(bannerSizeAtom);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    formState: {errors}
  } = useFormContext();

  const state =
      location.search !== ""
          ? queryString.parse(
              location.search
          )
          : location.state;

  const isCreateMode =
      !state?.campaignId;

  const [file, setFile] =
      useState(null);

  const [
    selectedMaterialType,
    setSelectedMaterialType
  ] = useState(
      MATERIAL_GROUP_TYPE.AUDIO
  );

  const [isLoading, setIsLoading] =
      useState(true);

  const [isSaving, setIsSaving] =
      useState(false);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      setIsLoading(true);

      try {
        const bannerSizeResponse =
            await selEnumInfo(
                "BANNER_SIZE"
            );

        if (mounted) {
          setBannerSize(
              bannerSizeResponse
                  ?.values ?? []
          );
        }

        if (isCreateMode) {
          const initialData = {
            ...campaignCreativeAtom.init,
            creativeType:
            MATERIAL_GROUP_TYPE.AUDIO,
            name: buildCreativeName(
                MATERIAL_GROUP_TYPE.AUDIO
            ),
            filePath:
                campaignCreativeAtom.init
                    ?.filePath ?? "",
            materials: [],
            nativeMaterials: [],
            logoPaths: []
          };

          if (mounted) {
            setSelectedMaterialType(
                MATERIAL_GROUP_TYPE.AUDIO
            );

            setCampaignCreative(
                initialData
            );

            setValue(
                "name",
                initialData.name
            );

            reset(initialData);
          }

          return;
        }

        const materialType =
            normalizeMaterialType(
                state?.creativeType
            );

        let response;

        switch (materialType) {
          case MATERIAL_GROUP_TYPE.BANNER:
            response =
                await selCreativeBannerInfo(
                    state.campaignId
                );
            break;

          case MATERIAL_GROUP_TYPE.LINK:
            response =
                await selCreativePopUnderInfo(
                    state.campaignId
                );
            break;

          case MATERIAL_GROUP_TYPE.AUDIO:
          default:
            response =
                await selCreativeAudioInfo(
                    state.campaignId
                );
            break;
        }

        if (!response) {
          throw new Error(
              "소재 정보를 불러오지 못했습니다."
          );
        }

        if (mounted) {
          const normalizedResponse = {
            ...response,
            materials:
                response.materials ?? [],
            nativeMaterials:
                response.nativeMaterials ??
                [],
            logoPaths:
                response.logoPaths ?? []
          };

          setSelectedMaterialType(
              materialType
          );

          setCampaignCreative(
              normalizedResponse
          );

          setCampaignBasicInfo(prev => ({
            ...prev,
            campaignId:
            state.campaignId,
            productType:
            state.productType
          }));

          reset(
              normalizedResponse
          );
        }
      } catch (error) {
        console.error(
            "소재 그룹 정보 조회 실패:",
            error
        );

        toast.error(
            "소재 그룹 정보를 불러오는 중 오류가 발생했습니다.",
            {
              toastId:
                  "creativeLoadError"
            }
        );
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (file) {
      clearErrors("file");
    }
  }, [file, clearErrors]);

  const handleSelectMaterialType =
      option => {
        if (option.disabled) {
          return;
        }

        const materialType =
            option.value;

        setSelectedMaterialType(
            materialType
        );

        const creativeName =
            buildCreativeName(
                materialType
            );

        setCampaignCreative(prev => ({
          ...prev,
          creativeType:
          materialType,
          name: creativeName
        }));

        setValue(
            "name",
            creativeName
        );

        clearErrors([
          "file",
          "materials",
          "pcLandingUrl",
          "mobLandingUrl"
        ]);
      };

  const handleChangeInput =
      event => {
        const {name, value} =
            event.target;

        setCampaignCreative(prev => ({
          ...prev,
          [name]: value
        }));
      };

  const onImageError = error => {
    if (error.maxFileSize) {
      toast.warning(
          "저장 가능한 이미지 크기는 10MB 이하입니다."
      );
      return;
    }

    if (error.maxNumber) {
      toast.warning(
          "이미지는 최대 5개까지 등록할 수 있습니다."
      );
      return;
    }

    if (error.acceptType) {
      toast.warning(
          "jpg, jpeg, gif, png 형식만 등록할 수 있습니다."
      );
    }
  };

  const validateBeforeSave = () => {
    if (
        selectedMaterialType ===
        MATERIAL_GROUP_TYPE.AUDIO
    ) {
      if (
          !file &&
          !campaignCreativeInfo
              ?.filePath
      ) {
        setError("file", {
          type: "required",
          message:
              "광고 소재 오디오 파일을 등록해주세요."
        });

        return false;
      }

      return true;
    }

    if (
        selectedMaterialType ===
        MATERIAL_GROUP_TYPE.BANNER
    ) {
      const hasEmptyMaterial =
          (
              campaignCreativeInfo
                  ?.materials ?? []
          ).some(
              material =>
                  (
                      material.images ?? []
                  ).length === 0
          );

      if (
          !campaignCreativeInfo
              ?.materials?.length ||
          hasEmptyMaterial
      ) {
        setError("materials", {
          type: "required",
          message:
              "배너 이미지를 한 개 이상 등록해주세요."
        });

        return false;
      }
    }

    return true;
  };

  const uploadAudio = async () => {
    if (!file) {
      return {
        filePath:
        campaignCreativeInfo
            ?.filePath,
        duration:
        campaignCreativeInfo
            ?.duration
      };
    }

    const response =
        await uploadAudioFile(file);

    if (!response?.uploadedFile) {
      throw new Error(
          "오디오 파일 업로드에 실패했습니다."
      );
    }

    return {
      filePath: response.path,
      duration:
      response.duration
    };
  };

  const saveCreative = async () => {
    const baseParam = {
      ...campaignCreativeInfo,
      campaignId:
          campaignBasicInfo
              ?.campaignId ??
          state?.campaignId,
      name:
          campaignCreativeInfo
              ?.name ??
          buildCreativeName(
              selectedMaterialType
          )
    };

    switch (selectedMaterialType) {
      case MATERIAL_GROUP_TYPE.BANNER:
        return updateCampaignBanner({
          ...baseParam,
          creativeType:
          MATERIAL_GROUP_TYPE.BANNER
        });

      case MATERIAL_GROUP_TYPE.LINK:
        return updateCampaignPopUnder({
          ...baseParam,
          creativeType:
              "POP_UNDER"
        });

      case MATERIAL_GROUP_TYPE.AUDIO:
      default: {
        const audioInfo =
            await uploadAudio();

        return updateCampaignAudio({
          ...baseParam,
          creativeType:
          MATERIAL_GROUP_TYPE.AUDIO,
          ...audioInfo,
          pcLandingUrl:
              baseParam.pcLandingUrl ||
              "http://dummy.com",
          mobLandingUrl:
              baseParam.mobLandingUrl ||
              "http://dummy.com"
        });
      }
    }
  };

  const onSubmit = async () => {
    if (isSaving) {
      return;
    }

    if (!validateBeforeSave()) {
      return;
    }

    setIsSaving(true);

    try {
      const response =
          await saveCreative();

      if (!response) {
        throw new Error(
            isCreateMode
                ? "소재 그룹 저장 실패"
                : "소재 그룹 수정 실패"
        );
      }

      if (isCreateMode) {
        toast.success(
            "소재 그룹이 저장되었습니다.",
            {
              toastId:
                  "creativeSaveSuccess",
              autoClose: 1000
            }
        );

        setStepCampaign({
          steps: 4
        });

        return;
      }

      toast.success(
          "소재 그룹이 수정되었습니다.",
          {
            toastId:
                "creativeUpdateSuccess",
            autoClose: 1000
          }
      );

      window.setTimeout(() => {
        if (state?.backLink) {
          navigate(
              "/board/manageCreative"
          );
          return;
        }

        navigate(
            "/board/dashboard"
        );
      }, 700);
    } catch (error) {
      console.error(
          "소재 그룹 저장 실패:",
          error
      );

      toast.error(
          isCreateMode
              ? "소재 그룹 저장 중 오류가 발생했습니다."
              : "소재 그룹 수정 중 오류가 발생했습니다.",
          {
            toastId:
                "creativeSaveError"
          }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onError = formErrors => {
    console.error(
        "소재 그룹 입력값 확인:",
        formErrors
    );
  };

  const handleCancel = () => {
    if (state?.campaignId) {
      window.history.back();
      return;
    }

    setStepCampaign({
      steps: 2
    });
  };

  const renderMaterialEditor = () => {
    switch (selectedMaterialType) {
      case MATERIAL_GROUP_TYPE.BANNER:
        return (
            <>
              <BannerImageEditor
                  bannerSizes={
                    bannerSizes
                  }
                  campaignCreativeInfo={
                    campaignCreativeInfo
                  }
                  setCampaignCreative={
                    setCampaignCreative
                  }
                  clearErrors={
                    clearErrors
                  }
                  errors={errors}
                  onImageError={
                    onImageError
                  }
              />

              <LandingUrlEditor
                  control={control}
                  errors={errors}
                  campaignCreativeInfo={
                    campaignCreativeInfo
                  }
                  onChangeInput={
                    handleChangeInput
                  }
                  title="배너 랜딩 URL 등록"
                  description="배너 클릭 시 이동할 PC 및 MOBILE URL을 입력해주세요."
              />
            </>
        );

      case MATERIAL_GROUP_TYPE.LINK:
        return (
            <LandingUrlEditor
                control={control}
                errors={errors}
                campaignCreativeInfo={
                  campaignCreativeInfo
                }
                onChangeInput={
                  handleChangeInput
                }
                title="링크 소재 등록"
                description="광고 클릭 시 이동할 PC 및 MOBILE URL을 입력해주세요."
            />
        );

      case MATERIAL_GROUP_TYPE.AUDIO:
      default:
        return (
            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  오디오 소재 등록
                </SectionTitle>

                <SectionGuide>
                  광고에 사용할 오디오 파일을 등록해주세요.
                </SectionGuide>
              </SectionHeader>

              <InnerBox>
                <AudioEditor
                    file={file}
                    setFile={setFile}
                    filePath={
                      campaignCreativeInfo
                          ?.filePath
                    }
                />

                {errors.file && (
                    <ValidationMessage>
                      {
                        errors.file
                            .message
                      }
                    </ValidationMessage>
                )}
              </InnerBox>
            </SettingCard>
        );
    }
  };

  if (isLoading) {
    return (
        <Board>
          <BoardHeader>
            소재 그룹 설정
          </BoardHeader>

          <LoadingArea>
            소재 그룹 정보를 불러오고 있습니다.
          </LoadingArea>
        </Board>
    );
  }

  return (
      <CampaignStepForm
          onSubmit={handleSubmit(
              onSubmit,
              onError
          )}
      >
        {params.id !==
            "manageCreativeDetail" &&
            state?.campaignId && (
                <AdverInfo>
            <span>
              광고주 정보
            </span>
                  <p />
                  <span>
              {state?.adverInfo}
            </span>
                </AdverInfo>
            )}

        <Board>
          <BoardHeader>
            소재 그룹 설정
          </BoardHeader>

          <BoardSearchResult>
            <SettingCard>
              <SectionHeader>
                <SectionTitle>
                  소재 그룹 선택
                </SectionTitle>

                <SectionGuide>
                  등록할 광고 소재 유형을 선택해주세요.
                </SectionGuide>
              </SectionHeader>

              <InnerBox>
                <MaterialTypeGrid>
                  {MATERIAL_GROUP_OPTIONS.map(
                      option => {
                        const selected =
                            selectedMaterialType ===
                            option.value;

                        return (
                            <MaterialTypeButton
                                key={
                                  option.value
                                }
                                type="button"
                                disabled={
                                  option.disabled
                                }
                                $selected={
                                  selected
                                }
                                onClick={() =>
                                    handleSelectMaterialType(
                                        option
                                    )
                                }
                            >
                              <MaterialTypeIcon
                                  $selected={
                                    selected
                                  }
                              >
                                {
                                  option.icon
                                }
                              </MaterialTypeIcon>

                              <MaterialTypeText>
                                <MaterialTypeTitle>
                                  {
                                    option.label
                                  }

                                  {option.disabled && (
                                      <DisabledBadge>
                                        준비 중
                                      </DisabledBadge>
                                  )}
                                </MaterialTypeTitle>

                                <MaterialTypeDescription>
                                  {
                                    option.description
                                  }
                                </MaterialTypeDescription>
                              </MaterialTypeText>

                              <MaterialRadio
                                  $selected={
                                    selected
                                  }
                              />
                            </MaterialTypeButton>
                        );
                      }
                  )}
                </MaterialTypeGrid>
              </InnerBox>
            </SettingCard>

            <MaterialEditorArea>
              {renderMaterialEditor()}
            </MaterialEditorArea>
          </BoardSearchResult>
        </Board>

        <CampaignStepSidebar>
          <CampaignSettingSummary
              currentStep={3}
              pendingAudioFile={file}
          />
          <SubmitContainer>
          <CancelButton
              type="button"
              disabled={isSaving}
              onClick={handleCancel}
          >
            {state?.campaignId
                ? "목록"
                : "이전"}
          </CancelButton>

          <SaveButton
              type="submit"
              disabled={isSaving}
          >
            {isSaving
                ? "처리 중"
                : state?.campaignId
                    ? "수정"
                    : "저장"}
          </SaveButton>
          </SubmitContainer>
        </CampaignStepSidebar>
      </CampaignStepForm>
  );
}

const LoadingArea = styled.div`
  display: flex;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 13px;
`;

const SettingCard = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: 15px;
  font-weight: 700;
`;

const SectionGuide = styled.div`
  color: #888;
  font-size: 12px;
  line-height: 1.5;
  text-align: right;
`;

const InnerBox = styled.div`
  padding: 22px 20px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #f9fafb;
`;

const MaterialTypeGrid = styled.div`
  display: grid;
  grid-template-columns:
    repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MaterialTypeButton = styled.button`
  position: relative;
  display: flex;
  min-height: 88px;
  align-items: center;
  gap: 13px;
  padding: 16px;
  border: 1px solid
  ${({$selected}) =>
          $selected
                  ? "#f5811f"
                  : "#e5e5e5"};
  border-radius: 8px;
  background-color:
          ${({$selected}) =>
                  $selected
                          ? "#fff8f1"
                          : "#fff"};
  text-align: left;
  cursor: pointer;
  transition:
          border-color 0.2s ease,
          background-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #f5811f;
    background-color: #fffaf6;
  }

  &:disabled {
    border-color: #e9e9e9;
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const MaterialTypeIcon = styled.span`
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background-color:
          ${({$selected}) =>
                  $selected
                          ? "#fff4e8"
                          : "#f1f1f1"};
  color:
          ${({$selected}) =>
                  $selected
                          ? "#f5811f"
                          : "#888"};
  font-size: 20px;
  font-weight: 700;
`;

const MaterialTypeText = styled.span`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 6px;
`;

const MaterialTypeTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #333;
  font-size: 14px;
  font-weight: 700;
`;

const MaterialTypeDescription = styled.span`
  color: #888;
  font-size: 11px;
  line-height: 1.4;
`;

const DisabledBadge = styled.span`
  display: inline-flex;
  min-height: 20px;
  align-items: center;
  padding: 0 7px;
  border-radius: 10px;
  background-color: #e9e9e9;
  color: #999;
  font-size: 9px;
  font-weight: 600;
`;

const MaterialRadio = styled.span`
  position: relative;
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border: 1px solid
  ${({$selected}) =>
          $selected
                  ? "#f5811f"
                  : "#ccc"};
  border-radius: 50%;
  background-color: #fff;

  &::after {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color:
            ${({$selected}) =>
                    $selected
                            ? "#f5811f"
                            : "transparent"};
    content: "";
  }
`;

const MaterialEditorArea = styled.div`
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 9px;
  }
`;

const FieldLabel = styled.div`
  width: 150px;
  min-width: 150px;
  padding-top: 10px;
  color: #444;
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 900px) {
    width: auto;
    min-width: 0;
    padding-top: 0;
  }
`;

const FieldContent = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 7px;
`;

const SettingInput = styled(Input)`
  width: 100%;
  min-width: 340px;
  max-width: 620px;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;

  &:focus {
    border-color: #f5811f;
  }

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 700px) {
    min-width: 0;
  }
`;

const ValidationMessage = styled.div`
  color: #e03131;
  font-size: 11px;
  line-height: 1.4;
`;

const BannerSizeButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BannerSizeButton = styled.button`
  height: 34px;
  padding: 0 13px;
  border: 1px solid
  ${({$active}) =>
          $active
                  ? "#f5811f"
                  : "#ddd"};
  border-radius: 17px;
  background-color:
          ${({$active}) =>
                  $active
                          ? "#fff4e8"
                          : "#fff"};
  color:
          ${({$active}) =>
                  $active
                          ? "#f5811f"
                          : "#666"};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #f5811f;
    color: #f5811f;
  }
`;

const ImageUploadArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ImageCard = styled.div`
  position: relative;
  width: 132px;
  height: 92px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 7px;
  background-color: #fff;
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
`;

const DeleteImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 15px;
  cursor: pointer;

  &:hover {
    background-color: #e03131;
  }
`;

const AddImageButton = styled.button`
  display: flex;
  width: 132px;
  height: 92px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px dashed #d5d5d5;
  border-radius: 7px;
  background-color: #fff;
  color: #888;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    border-color: #f5811f;
    background-color: #fffaf6;
    color: #f5811f;
  }
`;

const AddImageIcon = styled.span`
  font-size: 24px;
  line-height: 1;
`;

const SaveButton = styled.button`
  min-width: 84px;
  height: 38px;
  padding: 0 18px;
  border: 1px solid #f5811f;
  border-radius: 6px;
  background-color: #fff4e8;
  color: #f5811f;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;
